import { c as _c } from "react/compiler-runtime";
import React, { useMemo, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { RunHistoryChart } from "@/components/RunHistoryChart";
import { TestFailureCard } from "@/components/TestFailureCard";
import { TestLogViewer } from "@/components/TestLogViewer";
import { Skeleton } from "@/components/ui/skeleton";
import { assetUrl } from "@/lib/assetUrl";
import { BlacksmithLogomarkAnimationSVG } from "@/components/BlacksmithLogoSVG";
import type { TestHistoryResponse, TestHistoryRun } from "@/hooks/useTestHistory";
import { useStableCallback } from "@/hooks/useStableCallback";
import { useNowMinuteTick } from "@/components/agent-session/subtaskTicker";

// Helper function to parse timestamp as UTC (same logic used by RunHistoryChart)
// API returns timestamps like "2025-12-18 22:45:05" in UTC
const parseUTCTimestamp = (ts: string): Date => {
  // If already has timezone indicator, parse as-is
  if (ts.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(ts)) {
    return new Date(ts);
  }
  // Replace space with 'T' for ISO format and append 'Z' for UTC
  const isoFormat = ts.replace(" ", "T") + "Z";
  return new Date(isoFormat);
};
interface TestHistoryPanelProps {
  // Test history data
  testHistory: TestHistoryResponse | null;
  isLoadingHistory: boolean;
  isFetchingHistory?: boolean; // True when refetching in background with cached data shown
  testId: string;

  // Organization and repository info
  org: string;
  repository: string;
  runId: string;
  jobId: string;

  // Filter state (read-only, controlled by parent via TestDetailHeader)
  timeRangeHours: number;
  branchFilter: "all" | "main" | "current" | "main_and_current" | "latest_per_branch";
  currentBranch?: string | null;

  // Zoom state
  zoomStart: number | null;
  zoomEnd: number | null;
  onZoomChange: (start: number | null, end: number | null) => void;

  // Callbacks
  onRunClick: (run: TestHistoryRun) => void;

  // UI state
  isResizing: boolean;
  onNavigationStart?: (targetRunId: string, targetJobId: string) => void;
  showCardErrors: boolean;
  actorMap?: Map<string, string>;

  // Optional: Current Run Output props
  currentRunLogs?: string;
  currentRunDuration?: number;
  currentRunMaxDuration?: number;
  currentRunLogLineStart?: number;

  // User permissions
  isSuperuser?: boolean;
}
export const TestHistoryPanel = React.memo(function TestHistoryPanel(t0) {
  const $ = _c(49);
  const {
    testHistory,
    isLoadingHistory,
    isFetchingHistory: t1,
    testId,
    org,
    repository,
    runId,
    jobId,
    timeRangeHours,
    branchFilter,
    currentBranch,
    zoomStart,
    zoomEnd,
    onZoomChange,
    onRunClick,
    isResizing,
    onNavigationStart,
    showCardErrors,
    actorMap,
    currentRunLogs,
    currentRunDuration,
    currentRunMaxDuration,
    currentRunLogLineStart,
    isSuperuser: t2
  } = t0;
  const isFetchingHistory = t1 === undefined ? false : t1;
  const isSuperuser = t2 === undefined ? false : t2;
  const [hoveredFailureTestId, setHoveredFailureTestId] = useState(null);
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = hoveredId => {
      setHoveredFailureTestId(hoveredId);
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const handleFailureMouseEnter = useStableCallback(t3);
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setHoveredFailureTestId(null);
    };
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const handleFailureMouseLeave = useStableCallback(t4);
  const isChartLoading = isLoadingHistory || isFetchingHistory;
  const minuteNow = useNowMinuteTick(zoomStart == null || zoomEnd == null);
  let t5;
  bb0: {
    if (!testHistory?.runs) {
      let t6;
      if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = [];
        $[2] = t6;
      } else {
        t6 = $[2];
      }
      t5 = t6;
      break bb0;
    }
    const now = minuteNow + 60000;
    const startTime = now - timeRangeHours * 60 * 60 * 1000;
    const domainStart = zoomStart ?? startTime;
    const domainEnd = zoomEnd ?? now;
    let t6;
    if ($[3] !== domainEnd || $[4] !== domainStart || $[5] !== testHistory.runs || $[6] !== testId) {
      let t7;
      if ($[8] !== domainEnd || $[9] !== domainStart || $[10] !== testId) {
        t7 = run => {
          if (run.status !== "fail" && run.status !== "error") {
            return false;
          }
          if (run.test_id === testId) {
            return false;
          }
          if (!run.timestamp) {
            return false;
          }
          const timestamp = parseUTCTimestamp(run.timestamp).getTime();
          return timestamp >= domainStart && timestamp <= domainEnd;
        };
        $[8] = domainEnd;
        $[9] = domainStart;
        $[10] = testId;
        $[11] = t7;
      } else {
        t7 = $[11];
      }
      const failures = testHistory.runs.filter(t7);
      const seenTestIds = new Set();
      t6 = failures.filter(run_0 => {
        if (!run_0.test_id) {
          return true;
        }
        if (seenTestIds.has(run_0.test_id)) {
          return false;
        }
        seenTestIds.add(run_0.test_id);
        return true;
      });
      $[3] = domainEnd;
      $[4] = domainStart;
      $[5] = testHistory.runs;
      $[6] = testId;
      $[7] = t6;
    } else {
      t6 = $[7];
    }
    t5 = t6;
  }
  const failedRuns = t5;
  let t6;
  bb1: {
    if (!testHistory?.runs) {
      t6 = false;
      break bb1;
    }
    let t7;
    if ($[12] !== testHistory.runs || $[13] !== testId) {
      let t8;
      if ($[15] !== testId) {
        t8 = run_1 => (run_1.status === "fail" || run_1.status === "error") && run_1.test_id === testId;
        $[15] = testId;
        $[16] = t8;
      } else {
        t8 = $[16];
      }
      t7 = testHistory.runs.some(t8);
      $[12] = testHistory.runs;
      $[13] = testId;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    t6 = t7;
  }
  const isCurrentTestFailed = t6;
  let t7;
  bb2: {
    if (failedRuns.length === 0) {
      t7 = 0;
      break bb2;
    }
    let t8;
    if ($[17] !== failedRuns) {
      t8 = Math.max(...failedRuns.map(_temp));
      $[17] = failedRuns;
      $[18] = t8;
    } else {
      t8 = $[18];
    }
    t7 = t8;
  }
  const maxDurationMs = t7;
  let t8;
  if ($[19] !== actorMap || $[20] !== branchFilter || $[21] !== currentBranch || $[22] !== currentRunDuration || $[23] !== currentRunLogLineStart || $[24] !== currentRunLogs || $[25] !== currentRunMaxDuration || $[26] !== failedRuns || $[27] !== handleFailureMouseEnter || $[28] !== handleFailureMouseLeave || $[29] !== hoveredFailureTestId || $[30] !== isChartLoading || $[31] !== isCurrentTestFailed || $[32] !== isResizing || $[33] !== isSuperuser || $[34] !== jobId || $[35] !== maxDurationMs || $[36] !== onNavigationStart || $[37] !== onRunClick || $[38] !== onZoomChange || $[39] !== org || $[40] !== repository || $[41] !== runId || $[42] !== showCardErrors || $[43] !== testHistory?.runs || $[44] !== testId || $[45] !== timeRangeHours || $[46] !== zoomEnd || $[47] !== zoomStart) {
    t8 = <div className="flex flex-col h-full"><div className="px-4 py-4 flex-1 min-h-0">{(branchFilter === "current" || branchFilter === "main_and_current") && (!currentBranch || typeof currentBranch !== "string" || currentBranch.trim() === "") ? <div className="flex-1 flex items-center justify-center"><div className="text-center"><div className="text-sm text-[#737373] dark:text-neutral-400">Cannot filter by current branch: branch information not available for this run</div></div></div> : <>{currentRunLogs && <div className="mb-6"><div className="mb-4"><h2 className="pl-4 type-page-title-m text-neutral-900 dark:text-white">Test logs</h2></div><TestLogViewer logs={currentRunLogs} duration={currentRunDuration || 0} maxDuration={currentRunMaxDuration || 0} logLineStart={currentRunLogLineStart} mode="clamp" maxHeight={200} /></div>}<div className="mb-6"><div className="flex items-center justify-between mb-4"><div className="flex items-center gap-1.5 pl-4"><h2 className="type-page-title-m text-neutral-900 dark:text-white">Run History</h2></div>{!isChartLoading && zoomStart && zoomEnd && <button onClick={() => onZoomChange(null, null)} className="text-xs text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300">Reset to{" "}{[{
                  label: "3h",
                  hours: 3
                }, {
                  label: "12h",
                  hours: 12
                }, {
                  label: "1D",
                  hours: 24
                }, {
                  label: "2D",
                  hours: 48
                }, {
                  label: "5D",
                  hours: 120
                }, {
                  label: "14D",
                  hours: 336
                }, {
                  label: "30D",
                  hours: 720
                }, {
                  label: "3M",
                  hours: 2160
                }].find(r => r.hours === timeRangeHours)?.label || `${timeRangeHours}h`}</button>}</div>{isChartLoading ? <div className="h-[363px] w-full flex items-center justify-center bg-neutral-100/50 dark:bg-neutral-900/30 rounded-md"><div className="w-20 h-20"><BlacksmithLogomarkAnimationSVG /></div></div> : <RunHistoryChart runs={testHistory?.runs || []} org={org} runId={runId} jobId={jobId} testId={testId} currentRunId={runId} currentJobId={jobId} timeRangeHours={timeRangeHours} branchFilter={branchFilter} currentBranch={currentBranch ?? undefined} zoomStart={zoomStart} zoomEnd={zoomEnd} onZoomChange={onZoomChange} isResizing={isResizing} onNavigationStart={onNavigationStart} onRunClick={onRunClick} externalHoveredTestId={hoveredFailureTestId} />}</div><div className="mb-4"><div className="flex items-center"><h2 className="pl-4 type-page-title-m text-neutral-900 dark:text-white">Recent failures</h2></div></div>{isChartLoading ? <div className="space-y-3"><Skeleton className="h-24 w-full" /><Skeleton className="h-24 w-full" /></div> : failedRuns.length > 0 ? <div className="pb-16"><div className="border border-[var(--color-border)] rounded-xl overflow-hidden bg-[var(--color-surface)] divide-y divide-[var(--color-border)]">{failedRuns.map((run_3, index) => <TestFailureCard key={index} run={run_3} maxDurationMs={maxDurationMs} org={org} testId={testId} repository={repository} timeRangeHours={timeRangeHours} branchFilter={branchFilter} showErrorPreview={showCardErrors} actorMap={actorMap} onCardClick={onRunClick} isSuperuser={isSuperuser} onMouseEnter={handleFailureMouseEnter} onMouseLeave={handleFailureMouseLeave} />)}</div></div> : <div className="flex flex-1 flex-col items-center justify-center pt-0 pb-16"><div className="flex flex-col items-center justify-center gap-5 text-center w-full h-full px-12 py-[49px] rounded-lg border border-[var(--color-border)] dark:border-[var(--color-border)] bg-[var(--color-surface)]">{isCurrentTestFailed ? <div className="rounded-full bg-neutral-100 dark:bg-neutral-800 p-4"><CheckCircle2 className="w-8 h-8 text-neutral-400 dark:text-neutral-500" /></div> : <span aria-hidden={true} className="flex size-20 items-center justify-center"><img src={assetUrl("anvil-animations/anvil-zen-light.png")} alt="" width={80} height={80} draggable={false} className="select-none object-contain [image-rendering:pixelated] dark:hidden" /><img src={assetUrl("anvil-animations/anvil-zen-dark.png")} alt="" width={80} height={80} draggable={false} className="select-none object-contain [image-rendering:pixelated] hidden dark:block" /></span>}<div className="space-y-2"><p className="type-body-m-medium text-neutral-900 dark:text-neutral-100">{isCurrentTestFailed ? "No other recent failures" : "No recent failures found"}</p><p className="type-body-s-regular text-neutral-500 dark:text-neutral-400">{isCurrentTestFailed ? "This is the only recent failure for this test." : "This test has been passing in recent runs."}</p></div></div></div>}</>}</div></div>;
    $[19] = actorMap;
    $[20] = branchFilter;
    $[21] = currentBranch;
    $[22] = currentRunDuration;
    $[23] = currentRunLogLineStart;
    $[24] = currentRunLogs;
    $[25] = currentRunMaxDuration;
    $[26] = failedRuns;
    $[27] = handleFailureMouseEnter;
    $[28] = handleFailureMouseLeave;
    $[29] = hoveredFailureTestId;
    $[30] = isChartLoading;
    $[31] = isCurrentTestFailed;
    $[32] = isResizing;
    $[33] = isSuperuser;
    $[34] = jobId;
    $[35] = maxDurationMs;
    $[36] = onNavigationStart;
    $[37] = onRunClick;
    $[38] = onZoomChange;
    $[39] = org;
    $[40] = repository;
    $[41] = runId;
    $[42] = showCardErrors;
    $[43] = testHistory?.runs;
    $[44] = testId;
    $[45] = timeRangeHours;
    $[46] = zoomEnd;
    $[47] = zoomStart;
    $[48] = t8;
  } else {
    t8 = $[48];
  }
  return t8;
});
function _temp(run_2) {
  return Math.max(0, (run_2.duration_seconds ?? 0) * 1000);
}